/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useState } from "react";
import { BEM } from "@adapptio/lib-components";

import "./ReferenceGuideNav.scss";
import { NavLink } from "./NavLink";
import { ChildNav } from "./ChildNav";
import { REFERENCE_TYPE, TReferenceData } from "../../ReferenceGuideTypes";

export interface IReferenceGuideSidebarNavProps {
	className?: string;
	data: TReferenceData;
	selected: TReferenceData;
	setSelected: (select: TReferenceData) => void;
}

export const ReferenceGuideSidebarNav: React.FC<IReferenceGuideSidebarNavProps> = ({
	className,
	data,
	selected,
	setSelected
}) => {
	const baseClass = BEM("edt-reference-sidebar-nav");
	const [ activeParent, setActiveParent ] = useState<number[]>([]);

	const handleExpand = (e, item: TReferenceData, index: number) => {
		e.preventDefault();
		const newActiveParent = [ ...activeParent ];
		if (activeParent.indexOf(index) >= 0) {
			newActiveParent.splice(activeParent.indexOf(index), 1);
			setActiveParent(newActiveParent);
		} else {
			newActiveParent.push(index);
			setActiveParent(newActiveParent);
		}
	};

	const handleOpen = (e, item: TReferenceData) => {
		e.preventDefault();
		setSelected(item);
	};

	if (className) {
		baseClass.add(className);
	}

	return (
		<div className={baseClass.toClassName()}>
			{!!data.child?.length && (
				<h2 className={baseClass.element("category-name").toClassName()}>{data.label}</h2>
			)}
			{!!data.child?.length && (
				<ul>
					{data.child.map((item, index) => {
						if (item.child?.length) {
							return (
								<li
									key={index}
									className={baseClass
										.element("parent-item", { active: activeParent.indexOf(index) >= 0 })
										.toClassName()}
								>
									<NavLink
										expand
										key={"parent" + index}
										index={index}
										handle={handleExpand}
										item={item}
										label={item.label}
										// descriptor={item.getTypeDescriptor ? item.getTypeDescriptor() : null}
									/>

									{item.type === REFERENCE_TYPE.INTEGRATIONS ? (
										<ChildNav
											index={index}
											key={"child" + index}
											data={[ item ]}
											handle={handleOpen}
											selected={selected}
											label={"Config"}
											activeParent={activeParent}
										/>
									) : item.child?.length ? (
										<ChildNav
											index={index}
											key={"child2" + index}
											data={item.child}
											selected={selected}
											handle={handleOpen}
											activeParent={activeParent}
										/>
									) : null}

									{!!item.functions?.length && (
										<ChildNav
											index={index}
											data={item.functions}
											key={"child3" + index}
											selected={selected}
											handle={handleOpen}
											activeParent={activeParent}
										/>
									)}
								</li>
							);
						} else if (
							item.type === REFERENCE_TYPE.ACTIONS ||
							item.type === REFERENCE_TYPE.EVENTS ||
							item.type === REFERENCE_TYPE.DATASOURCE
						) {
							return (
								<li
									key={index}
									className={baseClass
										.element("single-item", { active: activeParent.indexOf(index) >= 0 })
										.toClassName()}
								>
									<NavLink
										key={"parentAcc" + index}
										index={index}
										handle={handleOpen}
										item={item}
										label={item.label}
										// descriptor={item.getTypeDescriptor ? item.getTypeDescriptor() : null}
									/>
								</li>
							);
						} else {
							return null;
						}
					})}
				</ul>
			)}
		</div>
	);
};
